import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, BehaviorSubject, Subscription } from "rxjs";

import { CookieService } from "angular2-cookie/services/cookies.service";

import { Constants } from "../../model/constants";

import { QuoteInfoService } from "../../services/quote-info.service";
import { CurrentUserService } from "../../services/current-user.service";
import { ApplicationStateService } from '../../services/application-state.service';
import { BrowserWidthService } from '../../services/browser-width.service';
import { ModelService } from "../../services/model.service";

@Component({
    selector: 'side-nav',
    template: require('./side-nav.component.html'),
    styles: [require('./side-nav.component.scss')]
})
export class SideNavComponent implements OnInit, OnDestroy {
    categories: Array<any>;
    subCategories: Array<any> = Constants.MchNavCategories.filter(nav => nav.isHidden===false);
    moreFlyOutMenuItems: Array<any> = [];
    clientHeight: BehaviorSubject<number> = new BehaviorSubject(null);
    quoteId: number;
    retailerNumber: string;
    userId: number;
    cmhBaseUrl: string;
    prospectBaseUrl: string;
	canViewProspects: boolean = false;
    subscription: Subscription = new Subscription();

    constructor(
        private router: Router,
        private quoteInfoService: QuoteInfoService,
        private currentUserSerivce: CurrentUserService,
        private appStateService: ApplicationStateService,
        private cookieService: CookieService,
        private bw: BrowserWidthService,
        private modelService: ModelService,
	) {

		const windowLoadSub = Observable.fromEvent(window, 'load')
			.map((event: any) => { this.clientHeight.next(event.target.activeElement.clientHeight); })
			.subscribe(x => { });
		this.subscription.add(windowLoadSub);

		const currentUserSub = this.currentUserSerivce.currentUser$.subscribe(x => {
            this.retailerNumber = x.retailerNumber;
            this.userId = x.cmhUserId;

            let canViewProspects = (x.roles.includes('Prospects_User')) ? true : false;
            let canViewLeads = (x.roles.includes('Sales_Leads_Claim') || x.roles.includes('Sales_Leads_Viewer')) ? true : false;
            let canViewNPS = (x.roles.includes('Nps_Viewer')) ? true : false;
            let canViewScheduler = (x.roles.includes('ProductionSchedulerAdmin') || x.roles.includes('ProductionSchedulerUser')) ? true : false;
            let canViewQuickview = (x.roles.includes('ProductionSchedulerAdmin') || x.roles.includes('ProductionSchedulerUser')) ? true : false;
            let canViewDispatch = (x.roles.includes('TRNSchedulerAdmin') || x.roles.includes('TRNSchedulerUser')) ? true : false;
            let canViewRCO = (x.roles.includes('RCOSchedulerUser') || x.roles.includes('RCOSchedulerAdmin')) ? true : false;
            let canViewCommunications = (x.roles.includes('HbgCommunications')) ? true : false;
            let canViewFoundationPlanner = (x.roles.includes('FndPlannerModels') || x.roles.includes('FndPlannerOrders') || x.roles.includes('FndPlannerWizard')) ? true : false;

            if (x.currentRetailer) {
                if (x.currentRetailer.retailerType === 'C') {
                    canViewLeads = false;
                    canViewNPS = false;
                }
            }

            this.buildCategoriesForSpecificUsers(canViewLeads, canViewNPS, canViewProspects, canViewScheduler, canViewQuickview, canViewDispatch, canViewRCO, canViewCommunications, canViewFoundationPlanner);
        });
        this.subscription.add(currentUserSub);
    }

    ngOnInit() {
        const browserWidthSub = this.bw.width.subscribe(x => { this.clientHeight.next(x.outerHeight); });
        this.subscription.add(browserWidthSub);
        const quoteIdSub = this.quoteInfoService.quoteId$.subscribe(x => this.quoteId = x);
        this.subscription.add(quoteIdSub);
        const quoteSub = this.quoteInfoService.quote$.subscribe(x => this.quoteId = x.quoteId);
        this.subscription.add(quoteSub);
        this.cmhBaseUrl = this.cookieService.get('cmhBaseUrl');
        this.prospectBaseUrl = this.cookieService.get('prospectBaseUrl');
        this.buildMchCategories();
        this.modelService.navigateBackToProductLine$.next(false);
    }

    navigate(link: string, index: number, isExternal?: boolean) {
        let newLink = link.replace('undefined', '');
        if (isExternal && index !== this.maxNavItems - 2) {
            window.location.href = newLink;
        }
        this.toggleFlyOut();
    }

    navigateMch(url: string) {
        if (this.quoteInfoService.canAccessQuote$.value === true) {
            if (url.includes('customer-info')) {
                this.router.navigate([url, this.quoteId]);
            } else {
                if (url.includes('/sales/quotes')) {
                    window.location.href = url;
                } else {
                    if (url.includes('options-configuration')) {
                        let ssId = sessionStorage.getItem('categoryId');
                        this.quoteInfoService.categoryId$.next(Number(ssId));
                        if (ssId) {
                            if (this.canNavigate(url)) { this.router.navigate([url, this.quoteId]); }
                        } else {
                            if (this.canNavigate(url)) { this.router.navigate([url, this.quoteId]); }
                        }
                    } else {
                        if (url.includes('product-line') && window.location.href.includes('choose-model')) {
                            this.modelService.navigateBackToProductLine$.next(true);
                        }
                        if (this.canNavigate(url)) {
                            this.router.navigate([url, this.quoteId]);
                        }
                    }
                }
            }
        }

        this.toggleFlyOut('Quotes');
    }

    get maxNavItems(): number {
        let a: number;
        this.clientHeight.subscribe(x => {
            a = Math.floor((x - 75) / 75);
        });
        return a;
    }

    // Keeps the flyout aligned with the more button on the sidenav menu
    get moreFlyOutMenuTopPosition() {
        return `${(this.maxNavItems - 1) * 75}px`;
    }

    // Keeps the flyout menu aligned with the quotes button on the sidenav menu
    get flyOutMenuTopPosition() {
        let index = this.categories.findIndex(item => {
            return item.name === 'Quotes'
        });
        return `${index * 75 + 75}px`;
    }

    toggleFlyOut(category?) {
        if (category === 'Quotes') {
            let temp;
            this.quoteInfoService.showSideNavFlyOut$.subscribe(x => {
                temp = x;
            });
            this.quoteInfoService.showSideNavFlyOut$.next(!temp);
        }
    }

    toggleMoreFlyOut(category?) {
        if (this.moreFlyOutMenuItems.length === 0) {
            this.buildFlyOut();
        } else {
            this.moreFlyOutMenuItems.length = 0;
        }
    }

    buildFlyOut() {
        this.moreFlyOutMenuItems = this.categories.slice(this.maxNavItems - 2);
    }

    buildCategoriesForSpecificUsers(canViewLeads: boolean, canViewNPS: boolean, canViewProspects: boolean,
        canViewScheduler: boolean, canViewQuickview: boolean, canViewDispatch: boolean, canViewRCO: boolean, 
        canViewCommunications: boolean, canViewFoundationPlanner: boolean) {
        this.categories = Constants.Categories;

        if (!canViewProspects) { this.categories = this.categories.filter(x => x.name != 'Prospects'); }
        if (!canViewNPS) { this.categories = this.categories.filter(x => x.name != 'NPS'); }
        if (!canViewLeads) { this.categories = this.categories.filter(x => x.name != 'Leads'); }
        if (!canViewScheduler) { this.categories = this.categories.filter(x => x.name != 'Prod Scheduler'); }
        if (!canViewQuickview) { this.categories = this.categories.filter(x => x.name != 'Prod Quickview'); }
        if (!canViewDispatch) { this.categories = this.categories.filter(x => x.name != 'Dispatch'); }
        if (!canViewRCO) { this.categories = this.categories.filter(x => x.name != 'RCO Scheduler'); }
        if (!canViewCommunications) { this.categories = this.categories.filter(x => x.name != 'Communications'); }
        if (!canViewFoundationPlanner) { this.categories = this.categories.filter(x => x.name != 'Foundation Planner'); }

            this.currentUserSerivce.getPartnerPortal().subscribe(partnerPortal => { 
            if (partnerPortal) {
                { this.categories = this.categories.filter(x => x.name != 'Inventory'); }
                { this.categories = this.categories.filter(x => x.name != 'My Homeowners'); }
                { this.categories = this.categories.filter(x => x.name != 'Service & Parts'); }
            } else if (!partnerPortal) {
                {this.categories = this.categories.filter(x => x.name != 'Service Center');}
            }
        })
        this.categories.forEach(x => {
            x.link = `${this.cmhBaseUrl}${x.link}`;
            if (x.name === 'Prospects') {
                x.link = `${this.prospectBaseUrl}/crm/web/prospects`;
            }
            if (x.name === 'Communications') {
                x.link = this.cookieService.get('commUrl');
            }
            if (x.name === 'Foundation Planner') {
                const foundationPlannerLink = this.generateAwsLink(`fndplanner`);
                x.link = foundationPlannerLink;
            }
        })
    }


    private generateAwsLink(app: string) {
        const { host, protocol } = window.location;
        const urlRegex = /(dev\.|itg\.|qua\.)*cmhconnection\.com/;
        const matches = host.match(urlRegex);

        let site: string;
        if (!matches) {
            // Local Host
            site = 'dev.cmhconnection.com';
        } else {
            site = matches[0];
        }

        return `${protocol}//${app}.${site}`;
    }

    buildMchCategories() {
        this.subCategories.forEach(x => {
            if (x.name === 'Quote List') {
                x.link = `${this.cmhBaseUrl}${x.link}`;
            }
        });
    }

    canNavigate(url: string): boolean {
        let canProceed = false;
        let quote = this.appStateService.getQuote();
        switch (url) {
            case 'choose-product-line':
                if (quote.quoteId > 0) { canProceed = true; }
                break;
            case 'choose-model':
                if (quote.productLineId > 0) { canProceed = true; }
                break;
            case 'options-configuration':
                if (quote.modelId > 0) { canProceed = true; }
                break;
            case 'set-markup':
                if (quote.modelId > 0) { canProceed = true; }
                break;
            case 'order-information':
                if (quote.modelId > 0) { canProceed = true; }
                break;
            case 'quote-summary':
                if (quote.modelId > 0) { canProceed = true; }
                break;
            default:
                canProceed = true;
                break;
        }

        return canProceed;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/reusable-components/side-nav/side-nav.component.ts